/* eslint-disable sort-keys */
/* eslint-disable max-lines */
import {
    ChevronRightIcon, DotsVerticalIcon,
} from '../../Icon'

import {
    Dropdown,
    DropdownButtonNew,
    DropdownContent,
} from '../../Dropdown'

import {
    MessageActionTypes, MessageProps,
    MessageRights,
} from '../types'

import {
    NinchatAttributes, NinchatState,
} from 'api/types'

import React, {useContext} from 'react'

import {
    datetime, time,
} from 'utils/datetime'

import AccessMessage from '../AccessMessage'
import Button from '../../Button'
import ChannelMessage from '../ChannelMessage'
import ConfirmationPromiseModal from 'components/Modal/ConfirmationPromiseModal'
import DeletedMessage from '../DeletedMessage'

import DropdownButton from '../../Dropdown/DropdownButtonNew'
import DropdownNestedMenu from 'components/Dropdown/DropdownNestedMenu'
import DropdownNestedMenuContent from 'components/Dropdown/DropdownNestedMenuContent'
import {DropdownState} from '../../Dropdown/types'
import {EmbedContext} from 'components/EmbedContext'
import InfoMessage from '../InfoMessage'
import MetadataMessage from '../MetadataMessage'
import {NinchatUserQueue} from 'api/types/properties'
import UIMessage from '../UIActionComposeMessage'

import {invitationUrlRegExp} from 'utils/regexp'
import {mapMessageActionToModalTitleText} from '../helpers'
import {styled} from '../../../stitches/config'
import {useAppSelector} from 'hooks'
import {usePromiseModal} from 'components/Modal/PromiseModal/hooks/usePromiseModal'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'


const userIcon = (ninchat: NinchatState, userId?: string): string | undefined => {
    if (userId === ninchat.user_id) {
        return ninchat.user_attrs?.iconurl
    }
    return userId && ninchat?._users?.[userId]?.user_attrs?.iconurl
}
const isEqual = (left: string | undefined | null, right: string | undefined | null) => left === right

// eslint-disable-next-line complexity
const Messenger = ({
    channelId,
    currentQueue,
    isLast,
    isMe,
    isRepeated,
    message,
    showHidden,
    rights,
    timeOnly,
    onRemovePendingMessage,
    onRetryMessage,
}: {
    channelId: string,
    currentQueue?: NinchatUserQueue | null,
    timeOnly: boolean,
    isLast?: boolean,
    isMe: boolean,
    isRepeated: boolean,
    message: MessageProps,
    showHidden: boolean,
    rights: MessageRights,
    onRemovePendingMessage: ((messageId: string) => void) | undefined,
    onRetryMessage: ((message: MessageProps) => void) | undefined,
}): (React.ReactElement | null) => {
    const iconUrl = useSelector(({ninchat}: {ninchat: NinchatState}) => userIcon(ninchat, message.message_user_id), isEqual)

    const props = {
        channelId,
        currentQueue,
        iconUrl: iconUrl || message._icon || '',
        isLast,
        isMe,
        isRepeated,
        message,
        rights,
        time: timeOnly ? time(message.message_time ?? 0) : datetime(message.message_time ?? 0),
        onRemovePendingMessage,
        onRetryMessage,
    }

    if (message.message_deleted) {
        return <DeletedMessage {...props} />
    }

    if (message.message_hidden && !showHidden) {
        return null
    }

    if (message.message_type === 'ninchat.com/text' && message.text?.match(invitationUrlRegExp)) {
        return <AccessMessage {...props} />
    }

    switch (message.message_type) {
        case 'ninchat.com/ui/action':
        case 'ninchat.com/ui/compose':
            return <UIMessage {...props} />
        case 'ninchat.com/github/pull_request':
        case 'ninchat.com/github/push':
        case 'ninch.at/deployment':
            return <ChannelMessage {...props} />
        case 'ninchat.com/info/channel':
        case 'ninchat.com/info/join':
        case 'ninchat.com/info/part':
        case 'ninchat.com/info/transfer/source':
        case 'ninchat.com/info/transfer/target':
        case 'ninchat.com/info/user':
        case 'ninchat.com/info/member':
            return <InfoMessage {...props} />
        case 'ninchat.com/info/access':
            return <AccessMessage {...props} />
        case 'ninchat.com/file':
        case 'ninchat.com/json':
        case 'ninchat.com/text':
            return <ChannelMessage {...props} />
        case 'ninchat.com/metadata':
            return <MetadataMessage {...props} />
        case 'ninchat.com/writing':
            return <ChannelMessage isWriting={true} {...props} />
        default:
            return null
    }
}

Messenger.defaultProps = {
    currentQueue: null,
    isLast: false,
}

const actionsSpacing = '2px'

const StyledActions = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: actionsSpacing,
    marginTop: '1.8rem',
    variants: {
        dropdownOpen: {true: {visibility: 'visible'}},
        me: {
            true: {
                marginLeft: 0,
                marginRight: actionsSpacing,
            },
        },
        repeated: {true: {marginTop: 0}},
    },
    visibility: 'hidden',
})

const StyledActionWrapper = styled('div', {
    '&:hover': {[`& ${StyledActions}`]: {visibility: 'visible'}},
    'alignItems': 'center',
    'display': 'flex',
    'marginTop': '8px',
    'compoundVariants': [
        {
            isEmbedQueueChannel: true,
            rights: 'other',
            css: {
                '[data-isletteravatar="true"] > div': {
                    borderColor: '$messageAvatarOtherBorder',
                    color: '$messageAvatarOtherText',
                },
            },
        },
        {
            isEmbedQueueChannel: true,
            rights: 'moderatorOperator',
            css: {
                '[data-isletteravatar="true"] > div': {
                    borderColor: '$messageAvatarOperatorModeratorBorder',
                    color: '$messageAvatarOperatorModeratorText',
                },
            },
        },
    ],
    'variants': {
        isEmbedQueueChannel: {
            true: {},
            false: {},
        },
        isAccessInfoMessage: {true: {justifyContent: 'center'}},
        me: {
            true: {
                '.compact &': {flexDirection: 'row'},
                'flexDirection': 'row-reverse',
            },
        },
        repeated: {true: {marginTop: '0.2rem'}},
        rights: {
            other: {},
            moderatorOperator: {},
        },
    },
})

const StyledItem = styled('div', {display: 'flex'})

const StyledButton = styled('div', {button: {width: '100%'}})

const Message = ({
    channelId,
    currentQueue,
    isAdmin,
    isAudienceChannel,
    isChannel,
    isMe,
    isModerator,
    isRepeated,
    isLast,
    memberAttrs,
    message: currentMessage,
    onDeleteMessage,
    onRemovePendingMessage,
    onRetryMessage,
    onAction,
    showHidden,
    timeOnly,
}: {
    // eslint-disable-next-line react/require-default-props
    channelId?: string,
    currentQueue?: NinchatUserQueue | null,
    isAdmin: boolean,
    isAudienceChannel?: boolean,
    isChannel?: boolean,
    isMe: boolean,
    isModerator?: boolean,
    isRepeated: boolean,
    isLast?: boolean,
    memberAttrs?: NinchatAttributes.NinchatChannelMemberAttributes
    message: MessageProps,
    onDeleteMessage?: (messageId: string) => void,
    onRemovePendingMessage?: (messageId: string) => void,
    onRetryMessage?: (message: MessageProps) => void,
    onAction?: (action: MessageActionTypes, message: MessageProps) => void,
    showHidden: boolean,
    timeOnly?: boolean,
}): React.ReactElement => {
    const {t} = useTranslation()
    const openConfirmationPromiseModal = usePromiseModal(ConfirmationPromiseModal)
    const isModerationActionConfirmationDisabled = useAppSelector(({ninchat}) => !!ninchat.user_settings.disable_moderation_action_confirmations)

    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)

    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    const onDropdownStateChange = (isOpen: DropdownState) => {
        setIsDropdownOpen(isOpen)
    }

    const handleChannelAction = (action: MessageActionTypes) => () => {
        if (isModerationActionConfirmationDisabled) {
            // eslint-disable-next-line no-unused-expressions
            onAction && onAction(action, currentMessage)
            return
        }

        const {
            title, text,
        } = mapMessageActionToModalTitleText(action, t)

        openConfirmationPromiseModal({
            text,
            title,
        }).then(() => {
            // eslint-disable-next-line no-unused-expressions
            onAction && onAction(action, currentMessage)
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        }, () => {})
    }

    const isDeletable = (m: MessageProps) => {
        const isDialogue = !isChannel && isMe
        const isChannelDeletable = isChannel && !isAudienceChannel && (isMe || isAdmin)

        if (m.message_hidden && !showHidden) {
            return false
        }

        return (isDialogue || isChannelDeletable) && (m.message_type === 'ninchat.com/file' || m.message_type === 'ninchat.com/text') && !m.message_deleted
    }

    const showContextMenu = (m: MessageProps) => isDeletable(m) ||
        // eslint-disable-next-line no-extra-parens, max-len
        (!isAudienceChannel && isModerator && !currentMessage.message_type.includes('/info/') && ((currentMessage.message_hidden && showHidden) || (!currentMessage.message_hidden)) && !currentMessage.message_deleted)

    const isAccessInfoMessage = currentMessage.message_type === 'ninchat.com/info/access'

    let rights: MessageRights = 'other'
    const isModeratorOperator = memberAttrs?.operator || memberAttrs?.moderator

    if (isEmbedQueueChannel) {
        const userId = currentMessage.message_user_id

        // addtional check for queue embed if a member who leave chat was operator or not
        embed?.embedState?.channelMembersAll?.forEach((channelMember) => {
            if (channelMember.id === userId) {
                if (channelMember.member_attrs?.operator || channelMember.member_attrs?.moderator) {
                    rights = 'moderatorOperator'
                }
            }
        })
    }

    if (isModeratorOperator) {
        rights = 'moderatorOperator'
    }

    return (
        <StyledActionWrapper isAccessInfoMessage={isAccessInfoMessage} isEmbedQueueChannel={isEmbedQueueChannel} me={isMe} repeated={isRepeated} rights={rights}>
            <Messenger
                channelId={channelId || ''}
                currentQueue={currentQueue}
                isLast={isLast}
                isMe={isMe}
                isRepeated={isRepeated}
                message={currentMessage}
                onRemovePendingMessage={onRemovePendingMessage}
                onRetryMessage={onRetryMessage}
                rights={rights}
                showHidden={showHidden}
                timeOnly={Boolean(timeOnly)}
            />
            {showContextMenu(currentMessage) &&
                <StyledActions dropdownOpen={isDropdownOpen} me={isMe} repeated={isRepeated}>
                    <Dropdown onStateChange={onDropdownStateChange}>
                        <DropdownButton caret={false} color='text' iconLeft={DotsVerticalIcon} size='small' />
                        <DropdownContent customClass='with-submenu' direction='fixed-end'>
                            <>
                                {/* eslint-disable-next-line no-extra-parens */}
                                {(isChannel && isModerator) ?
                                    <StyledButton>
                                        {memberAttrs?.silenced ?
                                            <StyledItem>
                                                <Button
                                                    color='text'
                                                    onPress={handleChannelAction(MessageActionTypes.unsilence)}
                                                    text={t('Unsilence user')}
                                                    type='button'
                                                />
                                            </StyledItem>
                                            :
                                            <DropdownNestedMenu>
                                                <DropdownButtonNew
                                                    color='text'
                                                    css={{
                                                        '> span': {
                                                            textAlign: 'left',
                                                            width: '100%',
                                                        },
                                                        'width': '100%',
                                                    }}
                                                    iconRight={ChevronRightIcon}
                                                    text={`${t('Silence user')}...`}
                                                    type='button'
                                                />
                                                <DropdownNestedMenuContent direction='left'>
                                                    <Button
                                                        color='text'
                                                        onPress={handleChannelAction(MessageActionTypes.silence)}
                                                        text={t('until unsilenced')}
                                                        type='button'
                                                    />
                                                    <Button
                                                        color='text'
                                                        onPress={handleChannelAction(MessageActionTypes.silence5m)}
                                                        text={t('for 5 minutes')}
                                                        type='button'
                                                    />
                                                    <Button
                                                        color='text'
                                                        onPress={handleChannelAction(MessageActionTypes.silence15m)}
                                                        text={t('for 15 minutes')}
                                                        type='button'
                                                    />
                                                    <Button
                                                        color='text'
                                                        onPress={handleChannelAction(MessageActionTypes.silence1h)}
                                                        text={t('for 1 hour')}
                                                        type='button'
                                                    />
                                                    <Button
                                                        color='text'
                                                        onPress={handleChannelAction(MessageActionTypes.silence24h)}
                                                        text={t('for 24 hours')}
                                                        type='button'
                                                    />
                                                </DropdownNestedMenuContent>
                                            </DropdownNestedMenu>}
                                        <StyledItem>
                                            {currentMessage.message_hidden ?
                                                <Button color='text' onPress={handleChannelAction(MessageActionTypes.showMessage)} text={t('Show this message')} /> :
                                                <Button color='text' onPress={handleChannelAction(MessageActionTypes.hideMessage)} text={t('Hide this message')} />}
                                        </StyledItem>
                                        <StyledItem>
                                            <Button color='text' onPress={handleChannelAction(MessageActionTypes.hideAllMessages)} text={t('Hide all messages from this user')} />
                                        </StyledItem>
                                        {memberAttrs?.autohide ?
                                            <StyledItem>
                                                <Button color='text' onPress={handleChannelAction(MessageActionTypes.showNewMessages)} text={t('Show new messages from this user')} />
                                            </StyledItem>
                                            :
                                            <StyledItem>
                                                <Button color='text' onPress={handleChannelAction(MessageActionTypes.hideNewMessages)} text={t('Hide new messages from this user')} />
                                            </StyledItem>}
                                        <StyledItem>
                                            <Button color='text' onPress={handleChannelAction(MessageActionTypes.removeUser)} text={t('Remove user from this channel')} />
                                        </StyledItem>
                                    </StyledButton>
                                    : null}
                                {isDeletable(currentMessage) &&
                                    <StyledItem>
                                        <Button color='text' onPress={() => { onDeleteMessage?.(currentMessage.message_id) }} text={t('Delete message')} />
                                    </StyledItem>}
                            </>
                        </DropdownContent>
                    </Dropdown>
                </StyledActions>}
        </StyledActionWrapper>
    )
}

Message.defaultProps = {
    currentQueue: null,
    isAudienceChannel: false,
    isChannel: undefined,
    isLast: false,
    isModerator: false,
    memberAttrs: {},
    onAction: undefined,
    onDeleteMessage: undefined,
    onRemovePendingMessage: undefined,
    onRetryMessage: undefined,
    timeOnly: false,
}

export default Message
